



























































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

enum DropdownPosition {
  TOP_RIGHT = 'is-top-right',
  TOP_LEFT = 'is-top-left',
  BOTTOM_RIGHT = 'is-bottom-right',
  BOTTOM_LEFT = 'is-bottom-left',
}

@Component({})
export default class DropdownMultipleSelect extends Vue {
  @Prop({ type: Array, default: () => [] })
  readonly value!: (string | number)[];
  @Prop({ type: Array, required: true })
  readonly options!: Array<{
    id: string | number; // string or number
    value: string;
  }>;
  @Prop({ type: String, required: true })
  readonly label!: string;
  @Prop({ type: String, default: 'Select...' })
  readonly placeholder!: string;
  @Prop({ type: Boolean, default: true })
  readonly multi!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly showSearch!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly disabled!: boolean;
  @Prop({ type: String, default: '' })
  readonly disableDataInfoMsg!: string;
  @Prop({ type: Array, default: () => [] })
  readonly disabledOptions!: (string | number)[]; // Expected format is always array of string or number
  @Prop({ type: String, default: DropdownPosition.BOTTOM_RIGHT })
  readonly position!: DropdownPosition;
  @Prop({ type: Boolean, default: false })
  readonly isRequired!: boolean;
  @Prop({ type: Boolean, required: false })
  public isCreateProject!: Boolean;

  public textLength: number = this.isCreateProject ? 120 : 60; // here values added based on UI components
  public searchQuery: string = '';
  public selectAllChecked: boolean = false;

  public doesIncludeSeparator(value: string): boolean {
    return value.includes('||');
  }

  public getValues(value: string): string[] {
    return value.split('||');
  }

  public getTruncateValue(optionValue: string): string {
    const value: string = this.doesIncludeSeparator(optionValue)
      ? this.getValues(optionValue)[1]
      : this.getValues(optionValue)[0];

    if (value.length > this.textLength) {
      return `${value.substring(0, this.textLength - 5)} ...`;
    }
    return value;
  }

  public getOptionTooltipValue(value: string) {
    const values: string[] = value.split('||');
    return values.join(' ');
  }

  get selectedOptions(): (string | number)[] {
    return this.value.map((valueId) => valueId);
  }

  set selectedOptions(ids: (string | number)[]) {
    this.$emit(
      'input',
      this.options.filter((o) => ids.includes(o.id)).map((o) => o.id),
    );
  }

  /** Filter options based on search query */
  get filteredOptions() {
    if (!this.searchQuery) return this.options;

    return this.options.filter((option) =>
      option.value.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );
  }

  /** Checks filter options that are NOT disabled, If there are no selectable options returns true */
  get isSelectAllDisabled() {
    const validSelectableOptions = this.filteredOptions.filter(
      (option) => !this.disabledOptions.includes(option.id),
    );
    return validSelectableOptions.length === 0;
  }

  get buttonDisplayText(): string {
    const selectedItems = this.selectedOptions.filter((op) =>
      this.selectedOptions.includes(op),
    );

    if (selectedItems.length === 0) return this.placeholder;
    if (selectedItems.length === this.options.length)
      return `All ${this.label}`;
    if (selectedItems.length === 1) {
      const getSelectedItem = this.options.find(
        (op) => op.id === selectedItems[0],
      );
      return getSelectedItem ? getSelectedItem.value : this.placeholder;
    }

    return `${selectedItems.length} ${this.label} selected`;
  }

  /** Show validation error if field is required but nothing selected */
  get showRequiredValidationError() {
    const selectedItems = this.options.filter((op) =>
      this.selectedOptions.includes(op.id),
    );

    return this.isRequired && this.options.length && !selectedItems.length;
  }

  /** Determine if an option should be disabled */
  private isOptionDisabled(option: {
    id: string | number; // string or number
    value: string;
    disabled?: boolean;
  }): boolean {
    return option.disabled === true || this.disabledOptions.includes(option.id);
  }

  /** Handle toggle an individual option's selection */
  private toggleOption(option: {
    id: string | number; // string or number
    value: string;
  }) {
    // For single-select mode, overwrite selection
    if (!this.multi) {
      if (this.selectedOptions.includes(option.id)) {
        this.selectedOptions = [];
      } else {
        this.selectedOptions = [option.id];
      }
      return;
    }

    // For multi-select, add or remove the option ID
    if (this.selectedOptions.includes(option.id)) {
      this.selectedOptions = this.selectedOptions
        .filter((selectedOption) => selectedOption !== option.id)
        .map((selectedOption) => selectedOption);
    } else {
      this.selectedOptions = [...this.selectedOptions, option.id];
    }

    this.syncSelectAll();
    this.$emit('input', this.selectedOptions);
  }

  /** Handle toggle 'Select All' functionality */
  private toggleSelectAll() {
    if (this.selectAllChecked) {
      /** remove all filtered options from selection */
      this.selectedOptions = this.options
        .filter((op) => {
          const a = this.filteredOptions.find((fop) => fop.id === op.id);
          if (!a) {
            return op.id;
          }
        })
        .map((op) => op.id);
    } else {
      /** add all enabled filtered options */
      const selectedItems = [...this.selectedOptions];
      this.filteredOptions.forEach((option) => {
        {
          if (
            !this.isOptionDisabled(option) &&
            !selectedItems.includes(option.id)
          ) {
            selectedItems.push(option.id);
          }
        }
      });

      this.selectedOptions = selectedItems;
    }

    /** update selectAll state and sync it */
    this.selectAllChecked = !this.selectAllChecked;
    this.syncSelectAll();
  }

  /** Sync 'Select All' checkbox state based on current selection */
  private syncSelectAll() {
    const selectedFilteredOptions = this.filteredOptions.filter((fp) =>
      this.selectedOptions.includes(fp.id),
    );

    this.selectAllChecked =
      this.filteredOptions.length > 0 &&
      selectedFilteredOptions.length === this.filteredOptions.length;
  }

  /** Reactively update Select All checkbox if value prop changes */
  @Watch('value', { immediate: true, deep: true })
  onValueChange() {
    this.syncSelectAll();
  }

  @Watch('searchQuery', { immediate: true, deep: true })
  onSearch() {
    this.syncSelectAll();
  }
}
